import { SecurityHelper, formatMessageTime, formatRelativeTime } from './helpers.js';
import { UserManager, createConversationManager } from './managers.js';
import { formatMessageContent } from './message.js';
import { createProductParser } from './product.js';
import { injectStyles, applyConfigVars } from './styles.js';
import { getTranslations } from './i18n.js';
import { detectStreaming } from './streaming.js';
import { buildTemplate, setupViewportSync, getWidgetElements, ICONS } from './widget-dom.js';
import { createListenerRegistry } from './listener-registry.js';
import { createConsentManager } from './consent.js';
import { createUnreadBadge } from './unread.js';
import {
  postChatMessage,
  extractReplyFromJson,
  extractFormFromJson,
  extractBlocksFromJson,
  postFormSubmission,
  postFormEvent,
  resolveErrorMessage
} from './transport.js';
import { createMessageRenderer } from './animation.js';
import {
  TOAST_DURATION,
  BUBBLE_ACTIVE_DURATION,
  INPUT_FOCUS_DELAY,
  RATE_LIMIT_INTERVAL,
  REQUEST_TIMEOUT_MS
} from './constants.js';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SINGLE_CHECK_SVG =
  '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const DOUBLE_CHECK_SVG =
  '<svg viewBox="0 0 20 16" aria-hidden="true"><path d="M2 8.5l3 3 6-6M8 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export function createWidget(config, apiBaseUrl, botId, options = {}) {
  const {
    container = document.body,
    embedded = false,
    alwaysOpen = false,
    ephemeral = false,
    skipConsent = false
  } = options;

  const t = getTranslations(config.lang);
  const productParser = createProductParser(apiBaseUrl);
  // Conversation-aware history. Delegates to the active conversation's bucket
  // while exposing the same load/addMessage/clear interface the renderer relies
  // on. Ephemeral mode (dashboard preview) keeps everything in memory so test
  // conversations never persist nor pollute a real visitor's history.
  const historyManager = createConversationManager(botId, undefined, { ephemeral });

  const el = document.createElement('div');
  el.id = 'chatyx-widget-root';
  if (embedded) el.classList.add('chatyx-embedded');
  container.appendChild(el);

  const listeners = createListenerRegistry();
  const themeController = injectStyles(el, config);
  listeners.registerCleanup(setupViewportSync(el));
  el.innerHTML += buildTemplate(config, t);

  const {
    chatBubble,
    chatWindow,
    chatMessages,
    userInput,
    sendButton,
    closeButton,
    newConversationButton,
    themeToggleButton,
    conversationsOpenButton,
    conversationsPanel,
    conversationsList,
    conversationsCloseButton,
    conversationsNewButton,
    newConvoModal,
    newConvoConfirmButton,
    newConvoCancelButton,
    consentAcceptButton,
    consentDeclineButton,
    consentCheckbox,
    unreadBadge
  } = getWidgetElements(el);

  // In preview mode the bot owner is testing their own bot, so the GDPR
  // consent gate (meant for end visitors) is bypassed with a stub.
  const consentManager = createConsentManager(el, skipConsent ? { getConsent: () => true } : UserManager);
  const unread = createUnreadBadge(unreadBadge, chatWindow);

  let lastSendTime = 0;
  let activeRequestController = null;
  let widgetDestroyed = false;
  let activeLeadForm = null;
  // True once a lead form is on screen and not yet submitted — used to emit an
  // `abandoned` telemetry signal if the form is dismissed without submission.
  let leadFormPending = false;

  // Best-effort lead-form telemetry. The `sent` event is recorded server-side
  // by n8n; here we emit the widget-side signals so the dashboard can tell a
  // render bug (sent but never rendered) from an abandonment (rendered, not submitted).
  function trackFormEvent(event, meta) {
    postFormEvent({
      apiBaseUrl,
      botId,
      userId: UserManager.getUserId(),
      sessionId: UserManager.getUserId(),
      event,
      meta
    });
  }

  function showToast(message, duration = TOAST_DURATION) {
    const existingToast = document.querySelector('.chat-toast');
    if (existingToast && existingToast.parentNode) {
      existingToast.parentNode.removeChild(existingToast);
    }
    const toast = document.createElement('div');
    toast.className = 'chat-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, duration);
  }

  function updateThemeToggleIcon() {
    const resolved = themeController.getResolved();
    if (resolved === 'dark') {
      themeToggleButton.innerHTML = ICONS.sun;
      themeToggleButton.title = t.lightMode;
      themeToggleButton.setAttribute('aria-label', t.lightMode);
    } else {
      themeToggleButton.innerHTML = ICONS.moon;
      themeToggleButton.title = t.darkMode;
      themeToggleButton.setAttribute('aria-label', t.darkMode);
    }
  }

  function createMessageMeta(type, timestamp, seen) {
    const timeText = formatMessageTime(timestamp, config.lang);
    if (!timeText && type !== 'user') return null;

    const meta = document.createElement('div');
    meta.className = `message-meta message-meta-${type}`;

    if (timeText) {
      const timeSpan = document.createElement('span');
      timeSpan.className = 'message-time';
      timeSpan.textContent = timeText;
      meta.appendChild(timeSpan);
    }

    if (type === 'user') {
      const check = document.createElement('span');
      check.className = seen ? 'message-check seen' : 'message-check';
      check.setAttribute('aria-label', seen ? t.messageSeen : t.messageSent);
      check.innerHTML = seen ? DOUBLE_CHECK_SVG : SINGLE_CHECK_SVG;
      meta.appendChild(check);
    }

    return meta;
  }

  function markPendingUserMessagesAsSeen() {
    const unseen = chatMessages.querySelectorAll('.message-meta-user .message-check:not(.seen)');
    unseen.forEach((check) => {
      check.classList.add('seen');
      check.setAttribute('aria-label', t.messageSeen);
      check.innerHTML = DOUBLE_CHECK_SVG;
    });
    historyManager.markUserMessagesAsSeen();
  }

  function createSuggestionButtons() {
    if (!Array.isArray(config.suggestions) || config.suggestions.length === 0) return null;
    const suggestionsDiv = document.createElement('div');
    suggestionsDiv.className = 'chat-suggestions';
    suggestionsDiv.id = 'chatyx-suggestions-active';
    config.suggestions.forEach((txt) => {
      if (txt && txt.trim && txt.trim().length) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = txt;
        btn.onclick = () => {
          if (!consentManager.checkAndShowConsent()) return;
          userInput.value = txt;
          sendButton.click();
          if (suggestionsDiv.parentNode) suggestionsDiv.parentNode.removeChild(suggestionsDiv);
        };
        suggestionsDiv.appendChild(btn);
      }
    });
    return suggestionsDiv;
  }

  function appendMessageToUI(type, content, saveToHistory = true, metadata = {}) {
    const row = document.createElement('div');
    row.className = `message-row message ${type} message-fade-in`;

    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    const avatarSrc = type === 'user' ? config.userAvatar : config.botAvatar;
    avatar.innerHTML = `<img src="${avatarSrc}" alt="">`;

    const bubble = document.createElement('div');
    bubble.className = 'bubble';

    if (type === 'user') {
      bubble.textContent = content;
    } else {
      bubble.innerHTML = SecurityHelper.sanitizeHTML(formatMessageContent(content));
    }

    row.appendChild(avatar);
    row.appendChild(bubble);
    chatMessages.appendChild(row);

    const timestamp = metadata.timestamp || new Date().toISOString();
    const seen = metadata.seen === true;
    const metaEl = createMessageMeta(type, timestamp, seen);
    if (metaEl) chatMessages.appendChild(metaEl);

    chatMessages.scrollTop = chatMessages.scrollHeight;

    if (saveToHistory) {
      historyManager.addMessage({ type, content, timestamp, seen });
    }
  }

  function loadAndDisplayHistory() {
    chatMessages.innerHTML = '';
    const history = historyManager.load();
    if (history.length === 0) {
      const welcomeRow = document.createElement('div');
      welcomeRow.className = 'message-row message bot';
      welcomeRow.innerHTML = `
        <div class="avatar"><img src="${config.botAvatar}" alt=""></div>
        <div class="bubble">${config.welcomeMessage}</div>
      `;
      chatMessages.appendChild(welcomeRow);
      const suggestions = createSuggestionButtons();
      if (suggestions) chatMessages.appendChild(suggestions);
    } else {
      history.forEach((msg, i) => {
        if (msg.kind === 'blocks' && Array.isArray(msg.blocks)) {
          renderer.displayBlocks(msg.blocks, { save: false });
          return;
        }
        let seen = msg.seen === true;
        if (!seen && msg.type === 'user') {
          seen = history.slice(i + 1).some((m) => m.type === 'bot');
        }
        appendMessageToUI(msg.type, msg.content, false, { timestamp: msg.timestamp, seen });
      });
    }
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  function showTyping() {
    const typingRow = document.createElement('div');
    typingRow.className = 'message-row message bot typing-row-temp';

    const avatar = document.createElement('div');
    avatar.className = 'avatar';
    avatar.innerHTML = `<img src="${config.botAvatar}" alt="">`;

    const bubble = document.createElement('div');
    bubble.className = 'bubble typing-bubble';
    bubble.setAttribute('aria-live', 'polite');
    bubble.setAttribute('aria-label', t.thinking);

    const shimmer = document.createElement('span');
    shimmer.className = 'typing-shimmer';
    shimmer.setAttribute('aria-hidden', 'true');

    const shimmerContent = document.createElement('span');
    shimmerContent.className = 'typing-shimmer-content';

    const shimmerLineMain = document.createElement('span');
    shimmerLineMain.className = 'typing-shimmer-line typing-shimmer-line-main';

    const shimmerLineSub = document.createElement('span');
    shimmerLineSub.className = 'typing-shimmer-line typing-shimmer-line-sub';

    shimmerContent.appendChild(shimmerLineMain);
    shimmerContent.appendChild(shimmerLineSub);
    shimmer.appendChild(shimmerContent);

    const text = document.createElement('span');
    text.className = 'typing-label typing-label-shimmer';
    text.textContent = t.thinking;

    bubble.appendChild(text);
    bubble.appendChild(shimmer);

    typingRow.appendChild(avatar);
    typingRow.appendChild(bubble);
    chatMessages.appendChild(typingRow);
    chatMessages.scrollTop = chatMessages.scrollHeight;
  }

  function removeTyping() {
    const typing = chatMessages.querySelector('.typing-row-temp');
    if (typing) typing.remove();
  }

  const renderer = createMessageRenderer({
    el,
    chatMessages,
    config,
    t,
    productParser,
    historyManager,
    removeTyping,
    markPendingUserMessagesAsSeen,
    createMessageMeta,
    incrementUnread: () => unread.increment()
  });

  function setComposerEnabled(enabled) {
    userInput.disabled = !enabled;
    sendButton.disabled = !enabled;
    if (enabled) userInput.focus();
  }

  function renderLeadFormFromAgent(formSchema, intro) {
    if (activeLeadForm) {
      if (leadFormPending) trackFormEvent('abandoned', { reason: 'replaced' });
      try {
        activeLeadForm.destroy();
      } catch (_) {
        /* noop */
      }
      activeLeadForm = null;
      leadFormPending = false;
    }
    setComposerEnabled(false);
    try {
      activeLeadForm = renderer.displayLeadForm({
        form: formSchema,
        intro,
        botId,
        onSubmit: handleLeadFormSubmit,
        onStart: () => trackFormEvent('started')
      });
      if (activeLeadForm) {
        leadFormPending = true;
        trackFormEvent('rendered');
      } else {
        // Schema normalisation rejected the form: the visitor sees the intro
        // text but no form — the exact "merci de remplir…" + nothing symptom.
        trackFormEvent('render_error', { reason: 'schema_rejected' });
      }
    } catch (err) {
      trackFormEvent('render_error', { reason: 'exception', message: err && err.message });
    }
  }

  async function handleLeadFormSubmit(submission) {
    const userId = UserManager.getUserId();
    const sessionId = userId; // n8n Postgres memory keys on session_id (=== userId today)
    try {
      const res = await postFormSubmission({
        apiBaseUrl,
        webhookUrl: config.webhookUrl,
        botId,
        userId,
        sessionId,
        formId: submission.formId,
        formVersion: submission.version,
        values: submission.values,
        consent: submission.consent,
        consentLabel: submission.consentLabel
      });
      let confirmation = '';
      try {
        const data = await res.clone().json();
        confirmation = extractReplyFromJson(data) || '';
      } catch (_) {
        /* server returned plain text or empty body */
      }

      leadFormPending = false;
      trackFormEvent('submitted');
      renderer.displaySubmittedSummaryAsUser(submission.summary || t.leadFormSummaryFallback);
      activeLeadForm = null;

      if (confirmation) {
        await renderer.displayBotResponseWithAnimation(confirmation, false);
      }

      setComposerEnabled(true);
      return { confirmation };
    } catch (err) {
      const code = (err && err.code) || 'unknown';
      const error = new Error('Form submission failed');
      error.userMessage = resolveErrorMessage(t, code);
      throw error;
    }
  }

  async function sendAndFetch(text) {
    if (!consentManager.checkAndShowConsent()) return;

    // If a lead form is still on screen and the user sends a new message instead
    // of filling it, count that as an abandonment (active, immediate signal).
    if (leadFormPending) {
      leadFormPending = false;
      trackFormEvent('abandoned', { reason: 'new_message' });
    }

    const activeSuggestions = chatMessages.querySelector('#chatyx-suggestions-active');
    if (activeSuggestions) activeSuggestions.remove();

    appendMessageToUI('user', text, true);
    userInput.value = '';
    userInput.disabled = true;
    sendButton.disabled = true;
    showTyping();

    if (activeRequestController) {
      try {
        activeRequestController.abort();
      } catch (_) {
        /* noop */
      }
    }
    const controller = new AbortController();
    activeRequestController = controller;
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const userId = UserManager.getUserId();
      const res = await postChatMessage({
        apiBaseUrl,
        webhookUrl: config.webhookUrl,
        message: text,
        botId,
        userId,
        signal: controller.signal
      });

      const detection = await detectStreaming(res);
      if (detection.isStream) {
        await renderer.displayBotResponseStreaming(detection.response, controller.signal);
        return;
      }

      const data = detection.json;
      const formSchema = extractFormFromJson(data);
      if (formSchema) {
        const intro = extractReplyFromJson(data) || '';
        renderLeadFormFromAgent(formSchema, intro);
        return;
      }
      // n8n sent a form envelope but it couldn't be parsed into a usable schema:
      // the visitor would only see the intro text and no form. Flag it as a bug.
      if (data && data.form) {
        trackFormEvent('render_error', { reason: 'parse_failed' });
      }
      if (data && data.products && Array.isArray(data.products) && data.products.length > 0) {
        await renderer.displayBotResponseWithAnimation(data, true);
        renderer.displayBlocks(extractBlocksFromJson(data));
        return;
      }

      const reply = extractReplyFromJson(data) || t.errorNoResponse;
      await renderer.displayBotResponseWithAnimation(reply, false);
      renderer.displayBlocks(extractBlocksFromJson(data));
    } catch (error) {
      if (error && (error.name === 'AbortError' || widgetDestroyed)) return;
      console.error('Erreur lors de la communication avec le webhook:', error);
      removeTyping();
      const code = (error && error.code) || 'unknown';
      await renderer.displayBotResponseWithAnimation(resolveErrorMessage(t, code), false);
    } finally {
      clearTimeout(timeoutId);
      if (activeRequestController === controller) activeRequestController = null;
      if (!widgetDestroyed && !activeLeadForm) {
        setComposerEnabled(true);
      }
    }
  }

  function handleSend() {
    const txt = userInput.value.trim();
    if (!txt.length) return;
    const now = Date.now();
    if (now - lastSendTime < RATE_LIMIT_INTERVAL) {
      showToast(t.rateLimited);
      return;
    }
    lastSendTime = now;
    sendAndFetch(txt);
  }

  function getFocusableElements() {
    return Array.from(chatWindow.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (node) => node.offsetParent !== null || node === document.activeElement
    );
  }

  function openChat() {
    chatWindow.classList.add('show');
    el.classList.add('chat-open');
    chatBubble.setAttribute('aria-expanded', 'true');
    unread.reset();
    consentManager.checkAndShowConsent();
    setTimeout(() => userInput.focus(), INPUT_FOCUS_DELAY);
  }

  function closeChat() {
    chatWindow.classList.remove('show');
    el.classList.remove('chat-open');
    chatBubble.setAttribute('aria-expanded', 'false');
    closeNewConvoModal();
    closeConversationsPanel();
    try {
      chatBubble.focus();
    } catch (_) {
      /* noop */
    }
  }

  // --- New-conversation confirmation modal (triggered by the refresh button) ---
  function openNewConvoModal() {
    newConvoModal.classList.add('show');
    setTimeout(() => {
      try {
        newConvoConfirmButton.focus();
      } catch (_) {
        /* noop */
      }
    }, INPUT_FOCUS_DELAY);
  }

  function closeNewConvoModal() {
    newConvoModal.classList.remove('show');
  }

  // Tear down any open lead form before swapping conversations so its pending
  // telemetry is flushed and the composer is re-enabled cleanly.
  function discardActiveLeadForm(reason) {
    if (!activeLeadForm) return;
    if (leadFormPending) trackFormEvent('abandoned', { reason });
    try {
      activeLeadForm.destroy();
    } catch (_) {
      /* noop */
    }
    activeLeadForm = null;
    leadFormPending = false;
  }

  function startNewConversation() {
    discardActiveLeadForm('new_conversation');
    historyManager.createConversation();
    loadAndDisplayHistory();
    setComposerEnabled(true);
    closeNewConvoModal();
    closeConversationsPanel();
    showToast(t.newConversationStarted);
  }

  function switchConversation(id) {
    if (id === historyManager.getActiveId()) {
      closeConversationsPanel();
      return;
    }
    discardActiveLeadForm('conversation_switched');
    historyManager.switchTo(id);
    loadAndDisplayHistory();
    setComposerEnabled(true);
    closeConversationsPanel();
  }

  // --- Conversations list ("page" within the widget) ---
  function renderConversationsList() {
    const conversations = historyManager.listConversations();
    conversationsList.innerHTML = '';

    if (conversations.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'chatyx-conversations-empty';
      empty.textContent = t.noConversations;
      conversationsList.appendChild(empty);
      return;
    }

    conversations.forEach((conv) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'chatyx-conversation-item' + (conv.active ? ' active' : '');
      item.setAttribute('role', 'listitem');
      item.dataset.conversationId = conv.id;

      const avatar = document.createElement('div');
      avatar.className = 'chatyx-conversation-avatar';
      avatar.innerHTML = `<img src="${config.botAvatar}" alt="">`;

      const body = document.createElement('div');
      body.className = 'chatyx-conversation-body';

      const preview = document.createElement('div');
      preview.className = 'chatyx-conversation-preview';
      preview.textContent = conv.preview || t.emptyConversation;

      const time = document.createElement('div');
      time.className = 'chatyx-conversation-time';
      time.textContent = formatRelativeTime(conv.updatedAt, config.lang);

      body.appendChild(preview);
      body.appendChild(time);

      const chevron = document.createElement('span');
      chevron.className = 'chatyx-conversation-chevron';
      chevron.innerHTML = ICONS.chevron;

      item.appendChild(avatar);
      item.appendChild(body);
      item.appendChild(chevron);
      conversationsList.appendChild(item);
    });
  }

  function openConversationsPanel() {
    renderConversationsList();
    conversationsPanel.classList.add('show');
    setTimeout(() => {
      try {
        conversationsCloseButton.focus();
      } catch (_) {
        /* noop */
      }
    }, INPUT_FOCUS_DELAY);
  }

  function closeConversationsPanel() {
    conversationsPanel.classList.remove('show');
  }

  // Event listeners
  listeners.add(consentCheckbox, 'change', () => {
    consentAcceptButton.disabled = !consentCheckbox.checked;
  });

  listeners.add(sendButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSend();
  });

  listeners.add(userInput, 'keydown', (e) => {
    if (e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      handleSend();
    }
  });

  listeners.add(chatBubble, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    chatBubble.classList.add('active');
    setTimeout(() => chatBubble.classList.remove('active'), BUBBLE_ACTIVE_DURATION);
    if (chatWindow.classList.contains('show')) closeChat();
    else openChat();
  });

  listeners.add(closeButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeChat();
  });

  listeners.add(document, 'keydown', (e) => {
    if (e.key !== 'Escape' || !chatWindow.classList.contains('show')) return;
    // Escape peels back one layer at a time: modal → conversations panel → chat.
    if (newConvoModal.classList.contains('show')) {
      closeNewConvoModal();
      return;
    }
    if (conversationsPanel.classList.contains('show')) {
      closeConversationsPanel();
      return;
    }
    closeChat();
  });

  listeners.add(chatWindow, 'keydown', (e) => {
    if (e.key !== 'Tab') return;
    if (!chatWindow.classList.contains('show')) return;
    const focusables = getFocusableElements();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  });

  // The refresh button no longer wipes history: it offers to start a fresh
  // conversation (the previous one is kept and reachable from the list).
  listeners.add(newConversationButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openNewConvoModal();
  });

  listeners.add(newConvoConfirmButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    startNewConversation();
  });

  listeners.add(newConvoCancelButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeNewConvoModal();
  });

  // Click on the backdrop (outside the card) dismisses the modal.
  listeners.add(newConvoModal, 'click', (e) => {
    if (e.target === newConvoModal) closeNewConvoModal();
  });

  listeners.add(conversationsOpenButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    openConversationsPanel();
  });

  listeners.add(conversationsCloseButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeConversationsPanel();
  });

  listeners.add(conversationsNewButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    startNewConversation();
  });

  // Delegated: pick a conversation from the list to switch to it.
  listeners.add(conversationsList, 'click', (e) => {
    const item = e.target.closest('.chatyx-conversation-item');
    if (!item || !item.dataset.conversationId) return;
    e.preventDefault();
    e.stopPropagation();
    switchConversation(item.dataset.conversationId);
  });

  listeners.add(themeToggleButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    themeController.toggle();
    updateThemeToggleIcon();
  });

  listeners.add(consentAcceptButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    UserManager.setConsent(true);
    consentManager.hideConsentScreen();
  });

  listeners.add(consentDeclineButton, 'click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    UserManager.setConsent(false);
    consentManager.hideConsentScreen();
    chatWindow.classList.remove('show');
  });

  // If the visitor leaves the page while a lead form is open and unsubmitted,
  // record an abandonment (sendBeacon survives unload).
  listeners.add(window, 'pagehide', () => {
    if (leadFormPending) {
      leadFormPending = false;
      trackFormEvent('abandoned', { reason: 'page_unload' });
    }
  });

  // Re-apply a new visual config in place, without tearing down the widget or
  // the ongoing conversation. Used by the live dashboard preview. Structural
  // changes (lang/theme) are handled by the caller via a full re-mount.
  function update(next) {
    // Mutate in place so later reads (new messages, suggestions, avatars)
    // pick up the new values automatically.
    Object.assign(config, next);

    applyConfigVars(el, config);

    // Theme is applied in place (no re-mount): re-resolve and re-apply the
    // theme class, then refresh the toggle icon.
    if (typeof themeController.setOwnerTheme === 'function') {
      themeController.setOwnerTheme(config.theme);
      updateThemeToggleIcon();
    }

    const title = el.querySelector('.chatbot-title');
    if (title) title.innerHTML = config.chatbotName;

    const headerAvatar = el.querySelector('.header-bot-avatar');
    if (headerAvatar) headerAvatar.innerHTML = `<img src="${config.botAvatar}" alt="">`;

    const bubbleImg = chatBubble.querySelector('img');
    if (bubbleImg) bubbleImg.outerHTML = `<img src="${config.bubbleIcon}" alt="">`;

    sendButton.innerHTML = `<img src="${config.sendIcon}" alt="">`;

    const branding = el.querySelector('.branding');
    if (branding) {
      if (config.showBranding) {
        branding.className = 'branding';
        branding.innerHTML = `${t.poweredBy} <strong>Chatyx</strong> 🚀`;
      } else {
        branding.className = 'branding hidden';
        branding.innerHTML = '';
      }
    }

    // Welcome message and suggestions only exist in the "empty" state, so only
    // refresh them when no conversation has started — never wipe live messages.
    if (historyManager.load().length === 0) {
      loadAndDisplayHistory();
    }
  }

  updateThemeToggleIcon();
  loadAndDisplayHistory();
  if (embedded || alwaysOpen) openChat();
  console.log('Chatyx Widget initialisé avec succès');

  return {
    destroy() {
      widgetDestroyed = true;
      if (activeRequestController) {
        try {
          activeRequestController.abort();
        } catch (_) {
          /* noop */
        }
        activeRequestController = null;
      }
      if (activeLeadForm) {
        if (leadFormPending) trackFormEvent('abandoned', { reason: 'destroyed' });
        try {
          activeLeadForm.destroy();
        } catch (_) {
          /* noop */
        }
        activeLeadForm = null;
        leadFormPending = false;
      }
      listeners.cleanupAll();
      if (el.parentNode) el.parentNode.removeChild(el);
    },
    update,
    open: openChat,
    close: closeChat
  };
}
